import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import StoreIcon from '@mui/icons-material/Store';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CampaignIcon from '@mui/icons-material/Campaign';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import api, { formatApiError } from '../api';
import { useAuth } from '../auth';
import { typeLabel, vendorCurrency } from '../constants';

function StatCard({ label, value, hint }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" fontWeight={800}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={900} sx={{ mt: 0.5 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

function classifyChannel(order) {
  const hay = [
    order?.hotel_name,
    order?.hotel_id,
    order?.room_number,
    order?.source,
    order?.channel,
    order?.order_source,
    order?.customer_type,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (
    hay.includes('hotel') ||
    order?.hotel_id ||
    order?.hotel_name ||
    order?.room_number
  ) {
    return 'hotel';
  }
  return 'local';
}

export default function DashboardPage() {
  const { vendor } = useAuth();
  const currency = vendorCurrency(vendor);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [productCount, setProductCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [orders, setOrders] = useState([]);
  const [wallet, setWallet] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [prodRes, ordersRes, financeRes] = await Promise.all([
        api.get('/api/vendor/products', { params: { include_inactive: true } }),
        api.get('/api/vendor/orders', { params: { limit: 50, summary: true } }).catch(() => ({
          data: [],
        })),
        api.get('/vendor/auth/finance/balance').catch(() => ({ data: null })),
      ]);
      const products = prodRes.data?.products || [];
      setProductCount(products.length);
      setActiveCount(products.filter((p) => p.is_active !== false).length);
      const list = Array.isArray(ordersRes.data)
        ? ordersRes.data
        : ordersRes.data?.orders || [];
      setOrders(list);
      setWallet(financeRes.data || null);
    } catch (err) {
      setError(formatApiError(err, 'Could not load dashboard'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sales = useMemo(() => {
    let hotel = 0;
    let local = 0;
    let revenue = 0;
    for (const o of orders) {
      const ch = classifyChannel(o);
      if (ch === 'hotel') hotel += 1;
      else local += 1;
      revenue += Number(o.total || o.total_amount || 0) || 0;
    }
    return { hotel, local, revenue, total: orders.length };
  }, [orders]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={900}>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {vendor?.name} · {typeLabel(vendor?.vendor_type)}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Active items" value={activeCount} hint={`${productCount} total`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Orders (recent)" value={sales.total} hint="Last loaded batch" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Via hotel" value={sales.hotel} hint="Guest / hotel channel" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Local customers" value={sales.local} hint="App / maps / walk-in" />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 0.75 }}>
          Prepaid wallet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, lineHeight: 1.5 }}>
          You keep what the customer pays you. GoSnel takes its share from this prepaid wallet
          (same model as restaurants).
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <StatCard
              label={`Balance (${currency})`}
              value={
                wallet?.balance != null ? Number(wallet.balance).toFixed(2) : '—'
              }
              hint="Top up via Stripe on Top up wallet"
            />
            <Button
              component={RouterLink}
              to="/topup"
              variant="contained"
              size="small"
              sx={{ mt: 1.25 }}
            >
              Top up wallet
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard
              label="GoSnel share"
              value={
                wallet?.commission_rate != null
                  ? `${Math.round(Number(wallet.commission_rate) * 1000) / 10}%`
                  : '—'
              }
              hint="Of order value, deducted from wallet"
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Paper
            component={RouterLink}
            to="/orders"
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              textDecoration: 'none',
              color: 'inherit',
              display: 'block',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(15,118,110,0.04)' },
            }}
          >
            <ReceiptLongIcon color="primary" />
            <Typography fontWeight={900} sx={{ mt: 0.75 }}>
              Resident orders
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Neighbour inbox with live chime
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper
            component={RouterLink}
            to="/ads"
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              textDecoration: 'none',
              color: 'inherit',
              display: 'block',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(15,118,110,0.04)' },
            }}
          >
            <CampaignIcon color="primary" />
            <Typography fontWeight={900} sx={{ mt: 0.75 }}>
              Promote / Ads
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Feature products on Neighbour banners
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper
            component={RouterLink}
            to="/ai"
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              textDecoration: 'none',
              color: 'inherit',
              display: 'block',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(15,118,110,0.04)' },
            }}
          >
            <SmartToyIcon color="primary" />
            <Typography fontWeight={900} sx={{ mt: 0.75 }}>
              GoSnel AI
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ask about shop tools & get support
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          Quick actions
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            component={RouterLink}
            to="/products"
            variant="contained"
            startIcon={<Inventory2Icon />}
          >
            Manage items
          </Button>
          <Button component={RouterLink} to="/orders" variant="outlined" startIcon={<ReceiptLongIcon />}>
            Resident orders
          </Button>
          <Button component={RouterLink} to="/ads" variant="outlined" startIcon={<CampaignIcon />}>
            Promote / Ads
          </Button>
          <Button component={RouterLink} to="/ai" variant="outlined" startIcon={<SmartToyIcon />}>
            GoSnel AI
          </Button>
          <Button component={RouterLink} to="/sales" variant="outlined" startIcon={<PointOfSaleIcon />}>
            View sales
          </Button>
          <Button component={RouterLink} to="/shop" variant="outlined" startIcon={<StoreIcon />}>
            Shop profile
          </Button>
        </Stack>
      </Paper>

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Sales split hotel vs local when order data includes hotel/room fields. Product WhatsApp
        orders may appear as local until hotel marketplace tagging is complete.
      </Alert>
    </Box>
  );
}
